/**
 * pasteTable.ts - The table paste pipeline.
 *
 * The public `pasteTable` method orchestrates the full flow given the shared
 * mutation params. The pure transform helpers below isolate the data
 * transformation portions so that they can be unit tested without rendering
 * `TableEditable`. Concerns that require component state - clipboard parsing and
 * file attachment detection - remain in the component and are threaded in via
 * the already-parsed cells argument.
 *
 * The pipeline splits into three stages, matching the original ordering in
 * `TableEditable._pasteTable`:
 *   1. `preparePastedCells` - transpose, column truncation, merged-cell shaping,
 *      and tiling so the pasted region at least fills the selection.
 *   2. `processPastedTypedValues` - normalize typed values based on whether the
 *      destination table supports them.
 *   3. `pasteTable` - write the prepared cells into the table via
 *      `excess.ops.paste`.
 */

import _ from 'underscore';

import {assert} from 'Asserts';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import checkSoftInvariant from 'lib/checkSoftInvariant';
import {resolveSelectorsFromPastedHTMLTables} from 'notebook/plain-tables/selectors/SelectorConflictResolver';
import {getTableLengthErrorMessage} from 'notebook/structured-tables/util/checkTableCapacity';
import isMergedCell from 'notebook/structured-tables/util/isMergedCell';
import {type PastedCell} from 'notebook/tables/constants/CellConstants';
import {type StructuredTableToTableMetadata, type Table} from 'notebook/tables/constants/TableConstants';
import * as TableLayoutConstants from 'notebook/tables/constants/TableLayoutConstants';
import {MultiSelectionBox, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import excess from 'notebook/tables/excess';
import {type EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import {type TableOpDiff} from 'notebook/tables/excess/ops/TableOpConstants';
import type {TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {computeAnyCellsLockedOrArchived} from 'notebook/tables/store/logic/TableLockingLogic';
import {computePasteRejectionReason} from 'notebook/tables/store/logic/TablePasteLogic';
import {computeExpandedSelectionBox} from 'notebook/tables/store/logic/TableSelectionLogic';
import TableUtil from 'notebook/tables/TableUtil';
import {extractSampleFromStageRunSample} from 'notebook/tables/typed-values/StageRunSampleUtil';
import transpose from 'notebook/tables/util/transpose';
import notifications from 'notifications';
import {trackContainerValidityForTableEvents} from 'samples/containers/ValidContentsUtils';

const {TABLE_MAX_COLUMNS} = TableLayoutConstants;

type PastedCells = Array<Array<PastedCell>>;

/** Options controlling how pasted cells are shaped to fit the destination selection. */
export interface PreparePastedCellsOptions {
  /** Whether to transpose the pasted data before tiling (context-menu "Paste transpose"). */
  transpose?: boolean;
  /** Whether merged cells in the paste source should be preserved. */
  preserveMergedCellsInPaste: boolean;
  /** Whether the destination table forbids adding custom columns (caps paste width). */
  disallowCustomColumns: boolean;
}

/** The result of writing prepared cells into a table. */
export interface PasteTableResult {
  /** The new table after the paste. */
  table: Table;
  /** The region that was pasted into. */
  box: MultiSelectionBox;
  /** Operation descriptor for undo/redo tracking. */
  tableOp: TableOpDiff;
}

/**
 * Whether the destination table supports typed values, i.e. any column has a
 * column definition constraint. Mirrors `TableEditable._areTypedValuesAllowed`.
 */
export function computeAreTypedValuesAllowed(table: Table): boolean {
  return table.columnConstraints.some((columnConstraint) =>
    Boolean(columnConstraint && columnConstraint.columnDefinition)
  );
}

function fillToDimension<T>(dim: number, array: Array<T>): Array<T> {
  // Output should have array at least once, even if it is more than dim.
  const out = array.slice();
  // Fill it as long as it is less than the dimension
  while (out.length + array.length <= dim) {
    out.push(...Array.from(array || []));
  }
  return out;
}

function expandMergedCellWithHiddenCells(
  cell: PastedCell,
  mergeRowSpan?: number,
  mergeColSpan?: number
): PastedCells {
  const result: PastedCells = [];
  for (let i = 0; i < (mergeRowSpan || 1); i++) {
    const row: Array<PastedCell> = [];
    for (let j = 0; j < (mergeColSpan || 1); j++) {
      if (i === 0 && j === 0) {
        row.push(cell);
      } else {
        row.push({text: ''});
      }
    }
    result.push(row);
  }
  return result;
}

/**
 * Shape the raw pasted `cells` so that they tile the destination selection `box`.
 *
 * Handles (in order): transpose, truncation to the table's max column count,
 * single/merged cell reshaping when pasting a single or fully-merged source into
 * a single or fully-merged destination, and tiling to fill the selection height
 * and width. Pure — extracted verbatim from `TableEditable._pasteTable`.
 *
 * @param table - The destination table.
 * @param computedTable - The evaluated destination table (for merged-box lookup).
 * @param cells - The raw pasted cells.
 * @param box - The merge-expanded destination selection box.
 * @param options - Transpose / merge-preservation / column constraints.
 * @returns The tiled cells ready for validation and pasting.
 */
export function preparePastedCells(
  table: Table,
  computedTable: EvaluatedTable,
  cells: PastedCells,
  box: MultiSelectionBox,
  options: PreparePastedCellsOptions
): PastedCells {
  let result = options.transpose ? transpose(cells) : cells;

  const {minI, maxI, minJ, maxJ} = box;
  const selH = maxI - minI + 1;
  const selW = maxJ - minJ + 1;

  const maxColumns = options.disallowCustomColumns ? table.table[0].length : TABLE_MAX_COLUMNS;

  // But first, truncate the data to paste if it results in a table wider than MAX_COLUMN
  const extraColumns = result[0].length + minJ - maxColumns;
  if (extraColumns > 0) {
    result = result.map((row) => row.slice(0, -extraColumns));
  }

  const isSingleOrMergedCell =
    (result.length === 1 && result[0].length === 1) ||
    ((result[0][0].mergeRowSpan ?? 1) === result.length &&
      (result[0][0].mergeColSpan ?? 1) === result[0].length);

  const destinationComputedCell = computedTable[minI][minJ];
  const destinationCell = table.table[minI][minJ];
  const isDestinationSingleOrMergedCell =
    (selH === 1 && selW === 1) ||
    Boolean(
      destinationComputedCell.mergedBox &&
      destinationComputedCell.mergedBox.minI === minI &&
      destinationComputedCell.mergedBox.maxI === maxI &&
      destinationComputedCell.mergedBox.minJ === minJ &&
      destinationComputedCell.mergedBox.maxJ === maxJ
    );

  if (isSingleOrMergedCell && isDestinationSingleOrMergedCell) {
    const sourceCell = result[0][0];
    // When pasting a merged cell into a single cell, just paste the value, not the merged cell
    if (options.preserveMergedCellsInPaste && isMergedCell(sourceCell)) {
      const {mergeColSpan, mergeRowSpan, ...pastedCell} = sourceCell;
      result = [[pastedCell]];
    }
    // When pasting a single cell into a merged cell, paste the value and keep the original merge.
    if (destinationCell && isMergedCell(destinationCell)) {
      const pastedCell: PastedCell = {...sourceCell};
      if (destinationCell.mergeColSpan) {
        pastedCell.mergeColSpan = destinationCell.mergeColSpan;
      }
      if (destinationCell.mergeRowSpan) {
        pastedCell.mergeRowSpan = destinationCell.mergeRowSpan;
      }
      result = expandMergedCellWithHiddenCells(pastedCell, pastedCell.mergeRowSpan, pastedCell.mergeColSpan);
    }
  }

  // Tile the (possibly reshaped) cells so they at least fill the selection.
  return fillToDimension(
    selH,
    result.map((row) => fillToDimension(selW, row))
  );
}

/**
 * Normalize typed values on the prepared cells based on whether the destination
 * table supports typed values. Mirrors the typed-value handling in
 * `TableEditable._pasteTable`.
 *
 * When typed values are allowed, cells carrying a typed value have their text
 * blanked (the typed value is authoritative). When not allowed, the `typedValue`
 * property is stripped entirely. Finally, any remaining typed values are reduced
 * to their underlying sample via `extractSampleFromStageRunSample`.
 *
 * @param table - The destination table (determines whether typed values are allowed).
 * @param cells - The prepared pasted cells.
 * @returns The cells with typed values normalized.
 */
export function processPastedTypedValues(table: Table, cells: PastedCells): PastedCells {
  const areTypedValuesAllowed = computeAreTypedValuesAllowed(table);

  let result: PastedCells;
  if (areTypedValuesAllowed) {
    // Empty out text if typedValue is specified.
    result = cells.map((row) => row.map((cell) => (cell.typedValue ? {...cell, text: ''} : cell)));
  } else {
    // eslint-disable-next-line no-restricted-syntax
    result = cells.map((row) => row.map((cell) => _.omit(cell, 'typedValue') as PastedCell));
  }

  return result.map((row) =>
    row.map((cell) =>
      cell.typedValue ? {...cell, typedValue: extractSampleFromStageRunSample(cell.typedValue)} : cell
    )
  );
}

/**
 * Write the prepared `cells` into `table` at (`minI`, `minJ`) via `excess.ops.paste`,
 * extending the table as needed. Pure wrapper preserving the original call in
 * `TableEditable._pasteTable`.
 *
 * @param table - The destination table.
 * @param computedTable - The evaluated destination table.
 * @param cells - The prepared, typed-value-normalized cells to paste.
 * @param minI - The top row at which to paste.
 * @param minJ - The left column at which to paste.
 * @param preserveMergedCellsInPaste - Whether to preserve merged cells while pasting.
 * @returns The new table, the region pasted into, and the table op.
 */
export function _pasteTableTransform(
  table: Table,
  computedTable: EvaluatedTable,
  cells: PastedCells,
  minI: number,
  minJ: number,
  preserveMergedCellsInPaste: boolean
): PasteTableResult {
  const {
    table: newTable,
    box,
    tableOp,
  } = excess.ops.paste(table, computedTable, cells, minI, minJ, preserveMergedCellsInPaste);
  return {table: newTable, box, tableOp};
}

/** Options for {@link pasteTable}. */
export interface PasteTableOptions {
  /** Whether to transpose the pasted data before pasting. */
  transpose?: boolean;
  /**
   * The cell index that originated the paste. Used for async pastes (e.g. context
   * menu) that may resolve after the active selection has changed.
   */
  selectionIndex?: TableIndex | null;
}

/**
 * Paste a 2D array of cells into the table.
 *
 * Orchestrates the full paste pipeline (excluding clipboard parsing and file attachment, which
 * remain in the component): resolves the destination selection, tiles/reshapes the pasted cells to
 * fill it, validates the paste (surfacing warnings/errors via notifications), normalizes typed
 * values, asynchronously resolves selectors, writes the cells via the pure `_pasteTableTransform`,
 * enforces capacity limits, remerges single-input columns, persists the change, and tracks analytics.
 *
 * @param params - The shared mutation dependencies.
 * @param cells - The 2D array of cells to paste (already parsed from the clipboard).
 * @param options - Paste options: `transpose` and the originating `selectionIndex`.
 * @param setSelection - Component callback to update the multi-selection. Called once with the
 *   expanded paste region (so a rejected paste is visible) and again with the final pasted region.
 */
export function pasteTable(
  params: TableMutationParams,
  cells: Array<Array<PastedCell>>,
  options: PasteTableOptions,
  setSelection: (args: {start: TableIndex; end: TableIndex; text?: string}) => void
): void {
  const model = params.getTableModel();
  const {
    table,
    computedTable,
    preserveMergedCellsInPaste,
    allowMergeSelection,
    selectorsLink,
    selectorsEnabled,
  } = model.props;
  const {multiStartIndex, multiEndIndex} = model.state;
  const {selectionIndex} = options;

  // Async paste (e.g. context menu) can resolve after the user clicks elsewhere,
  // so the current multi-selection may no longer match the cell that initiated the
  // paste. options.selectionIndex captures that originating cell. If it still falls
  // inside the active multi-selection, keep the full range so tile-fill works;
  // otherwise paste into just the originating cell.
  let selectionStart: TableIndex;
  let selectionEnd: TableIndex;
  if (
    selectionIndex != null &&
    multiStartIndex != null &&
    multiEndIndex != null &&
    TableUtil.isIndexBetween(multiStartIndex, multiEndIndex, selectionIndex)
  ) {
    selectionStart = multiStartIndex;
    selectionEnd = multiEndIndex;
  } else if (selectionIndex != null) {
    selectionStart = selectionIndex;
    selectionEnd = selectionIndex;
  } else if (multiStartIndex != null && multiEndIndex != null) {
    selectionStart = multiStartIndex;
    selectionEnd = multiEndIndex;
  } else {
    checkSoftInvariant(false, {
      message: 'Unable to paste: no selection or originating cell index available',
      expirationDate: '2027-04-01',
      errorInDevAndTest: true,
    });
    return;
  }

  const box = computeExpandedSelectionBox(selectionStart, selectionEnd, table, computedTable);
  // Matches the original `TableEditable._multiSelectionBox`, which hard-asserts a
  // non-null box in all environments (including production).
  assert(box != null, 'Expected to have a selection');

  // 1. Tile / reshape the pasted cells so they fill the selection.
  const preparedCells = preparePastedCells(table, computedTable, cells, box, {
    transpose: options.transpose,
    preserveMergedCellsInPaste: Boolean(preserveMergedCellsInPaste),
    disallowCustomColumns: Boolean(table.disallowCustomColumns),
  });

  // Set the selection according to the expanded paste table so that if it fails the user can see the issue
  setSelection({
    start: [box.minI, box.minJ],
    end: [
      Math.max(box.maxI, box.minI + preparedCells.length - 1),
      Math.max(box.maxJ, box.minJ + preparedCells[0].length - 1),
    ],
  });

  // 2. Validate the paste before mutating.
  const pasteRejection = computePasteRejectionReason(
    table,
    computedTable,
    preparedCells,
    box.minI,
    box.minJ,
    {
      supportsMerge: model.merging.supportsMerge,
      preserveMergedCellsInPaste: Boolean(preserveMergedCellsInPaste),
      allowMergeSelection: allowMergeSelection ?? undefined,
      isAnyCellLockedOrArchived: (lockCells) => computeAnyCellsLockedOrArchived(lockCells, table.rowData),
    }
  );
  if (pasteRejection != null) {
    if (pasteRejection.severity === 'warn') {
      notifications.warn(pasteRejection.reason);
    } else {
      notifications.error(pasteRejection.reason);
    }
    return;
  }

  // 3. Normalize typed values.
  const processedCells = processPastedTypedValues(table, preparedCells);

  // 4. Resolve selectors, then paste the tiled table at the top-left cell of the selection.
  resolveSelectorsFromPastedHTMLTables({
    pastedTables: [processedCells],
    existingSelectors: selectorsLink.value,
    // `selectorsEnabled` is optional and defaults to enabled, matching the
    // default applied to the `TableEditable` component props.
    selectorsEnabled: selectorsEnabled ?? true,
  }).then((selectors) => {
    // Selector resolution is async, so the table may have changed since the paste
    // was initiated. Re-read the latest model here so we paste against the current
    // table / computedTable / handlers, matching the original `_pasteTable` which
    // read `this.props.*` live inside the resolution callback. The prepared cells
    // and top-left paste coordinate (box.minI / box.minJ) are intentionally kept
    // from the initiation-time snapshot, exactly as the original did.
    const latestModel = params.getTableModel();
    const {
      table: latestTable,
      index: latestIndex,
      fileId: latestFileId,
      computedTable: latestComputedTable,
      preserveMergedCellsInPaste: latestPreserveMergedCellsInPaste,
      updateHandler: latestUpdateHandler,
    } = latestModel.props;

    const pasteResult = _pasteTableTransform(
      latestTable,
      latestComputedTable,
      processedCells,
      box.minI,
      box.minJ,
      Boolean(latestPreserveMergedCellsInPaste)
    );
    const {box: pasteBox, tableOp} = pasteResult;
    let {table: newTable} = pasteResult;

    const tableMetadata = latestTable.metadata
      ? (latestTable.metadata as StructuredTableToTableMetadata<unknown>)
      : undefined;

    // If tableMetadata is null, do not check for capacity here - it may be a plain table. Structured tables
    // will still catch the capacity issue before making changes, via a check in StructuredTablePlugin.setContent.
    const capacityErrorMsg =
      tableMetadata != null
        ? getTableLengthErrorMessage({
            currentTableLength: latestTable.table.length,
            newTableLength: newTable.table.length,
            structuredTableKind: tableMetadata.kind,
            isTableEditable: true,
            allowAddRows: latestModel.dimensions.canAddRows,
            allowRemoveRows: latestModel.dimensions.canRemoveRows,
          })
        : null;
    if (capacityErrorMsg) {
      notifications.error(capacityErrorMsg);
      return;
    }

    // TODO(T7414): Refactor into `excess/ops/TableOps`
    if (TableUtil.isInvalidOpWithWarn(latestTable, newTable)) {
      return;
    }

    // If any single inputs are present and the table is expanding, merge the single input columns accordingly.
    if (latestModel.singleInputs.boundaryIndex && newTable.table.length > latestTable.table.length) {
      // TODO(BNCH-136509): Possibly update tableOp to include single input merged cells?
      newTable = TableUtil.remergeSingleInputColumns(newTable);
    }

    // Update state (table and selectors) / selection
    const replacedCells = latestTable.table
      .slice(pasteBox.minI, pasteBox.maxI + 1)
      .flatMap((row) => row.slice(pasteBox.minJ, pasteBox.maxJ + 1));
    setSelection({
      start: [pasteBox.minI, pasteBox.minJ],
      end: [pasteBox.maxI, pasteBox.maxJ],
      text: newTable.table[pasteBox.minI][pasteBox.minJ].text,
    });
    latestUpdateHandler(latestIndex, {table: newTable}, selectors, {[newTable.name]: tableOp});

    // Track container validity analytics
    trackContainerValidityForTableEvents(latestFileId, latestTable.apiId!, [
      {
        event: ELN_ANALYTICS_ACTIONS.ADDED_CONTAINERS_TO_STRUCTURED_TABLE,
        cells: processedCells.flat(),
      },
      {event: ELN_ANALYTICS_ACTIONS.REMOVED_CONTAINERS_FROM_STRUCTURED_TABLE, cells: replacedCells},
    ]);
  });
}
